import React, { useEffect } from "react";
import { Link, Navigate, Route, Routes } from "react-router-dom";
import { useUserContext } from "../../context/UserProvider";
import { ArrowBackIcon, CalendarIcon } from "../util/Icons";
import SubMenuWrapper, { SubMenuItem } from "../util/SubMenu";

// Import training plan pages
import FiveKPlan from "./plans/5k";
import TenKPlan from "./plans/10k";
import HalfMarathonPlan from "./plans/half-marathon";
import MarathonPlan from "./plans/marathon";
import SixMinutePaceCalculator from "./components/SixMinutePaceCalculator";

// Training plans configuration
const PLANS = [
    {
        id: "5k",
        title: "5K Training Plan",
        description: "Build your endurance and speed for your first or fastest 5K.",
        icon: "🏃",
        path: "./5k",
        comingSoon: false,
    },
    {
        id: "10k",
        title: "10K Training Plan",
        description: "Take your running to the next level with structured 10K training.",
        icon: "🏃‍♀️",
        path: "./10k",
        hidden: true,
    },
    {
        id: "half-marathon",
        title: "Beginner Half Marathon Training Plan",
        description: "Prepare for your first half marathon with this beginner-friendly program.",
        icon: "🏃‍♀️",
        path: "./half-marathon",
        comingSoon: false,
    },
    {
        id: "six-minute-pace-calculator",
        title: "6 Minute Pace Calculator",
        description: "Estimate race times and training paces from your 6-minute test.",
        icon: "⏱️",
        path: "./six-minute-pace-calculator",
        comingSoon: false,
    },
    {
        id: "marathon",
        title: "Marathon Training Plan",
        description: "Conquer the 42.2km distance with expert marathon training.",
        icon: "🏆",
        path: "./marathon",
        hidden: true,
    },
];

export default function PlansMain() {
    const { canAccessHealthHub, membership } = useUserContext();

    useEffect(() => {
        document.title = "Training Plans - MCC";
    }, []);

    // Non-member view
    if (!canAccessHealthHub) {
        return (
            <div className="min-h-screen py-8 px-2 md:px-4" style={{ background: '#ffffff' }}>
                <div className="max-w-4xl mx-auto">
                    {/* SubMenu */}
                    <SubMenuWrapper>
                        <SubMenuItem icon={<ArrowBackIcon size={24} color="#6b7280" />} label={"Home"} to={"/"} />
                    </SubMenuWrapper>

                    <div className="text-center mt-8">
                        <div className="rounded-3xl p-8" style={{
                            background: 'linear-gradient(135deg, #f1f5f9 0%, #fed7aa 100%)',
                            border: '1px solid rgba(249, 115, 22, 0.15)'
                        }}>
                            <span className="text-6xl mb-4 block">🔒</span>
                            <h2 className="text-2xl font-bold mb-4" style={{ color: '#1e293b' }}>
                                Members Only Content
                            </h2>
                            <p className="text-base mb-6" style={{ color: '#64748b' }}>
                                Training Plans are available to members and athletes with a valid challenge registration. Unlock access to structured training programs for 5K, 10K, Half Marathon, and Marathon distances!
                            </p>
                            <Link
                                to="/membership"
                                className="inline-block rounded-2xl px-8 py-4 font-bold text-lg transition-all duration-300 hover:scale-[1.03] hover:shadow-2xl no-underline"
                                style={{
                                    background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                                    color: 'white',
                                    boxShadow: '0 10px 40px rgba(249, 115, 22, 0.3)'
                                }}
                            >
                                Become a Member
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Member view - Show training plans
    return (
        <Routes>
            <Route path="/" element={
                <div className="min-h-screen py-8 px-2 md:px-4" style={{ background: '#ffffff' }}>
                    <div className="max-w-4xl mx-auto">
                        {/* SubMenu */}
                        <SubMenuWrapper>
                            <SubMenuItem icon={<ArrowBackIcon size={24} color="#6b7280" />} label={"Home"} to={"/"} />
                        </SubMenuWrapper>

                        {/* Hero Section */}
                        <div className="text-center mb-6 mt-4">
                            <span className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-3" style={{
                                background: 'linear-gradient(90deg, #f97316 0%, #ea580c 100%)',
                                color: 'white'
                            }}>
                                📅 Training Plans
                            </span>
                            <h1 className="text-3xl md:text-4xl font-extrabold mb-3" style={{
                                background: 'linear-gradient(90deg, #1e293b 0%, #334155 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent'
                            }}>
                                Training Plans
                            </h1>
                            <p className="text-base md:text-lg" style={{ color: '#64748b' }}>
                                Structured programs to help you reach your race goals.
                            </p>
                        </div>

                        {/* Plans Grid */}
                        <div className="grid gap-4 md:grid-cols-2 md:gap-4">
                            {PLANS.filter((plan) => !plan.hidden).map((plan) => (
                                <PlanCard key={plan.id} plan={plan} isMember={membership === true} />
                            ))}
                        </div>
                    </div>
                </div>
            } />

            {/* Individual Plan Routes */}
            <Route path="/5k/*" element={<MembersOnlyPlanRoute isMember={membership === true}><FiveKPlan /></MembersOnlyPlanRoute>} />
            <Route path="/10k/*" element={<MembersOnlyPlanRoute isMember={membership === true}><TenKPlan /></MembersOnlyPlanRoute>} />
            <Route path="/half-marathon/*" element={<MembersOnlyPlanRoute isMember={membership === true}><HalfMarathonPlan /></MembersOnlyPlanRoute>} />
            <Route path="/marathon/*" element={<MembersOnlyPlanRoute isMember={membership === true}><MarathonPlan /></MembersOnlyPlanRoute>} />
            <Route path="/six-minute-pace-calculator/*" element={<MembersOnlyPlanRoute isMember={membership === true}><SixMinutePaceCalculator /></MembersOnlyPlanRoute>} />

            {/* If the path is anything else, redirect to here */}
            <Route path="/*" element={<Navigate to={"./"} />} />
        </Routes>
    );
}

function MembersOnlyPlanRoute({ children, isMember }) {
    if (isMember) {
        return children;
    }

    return (
        <div className="min-h-screen py-8 px-2 md:px-4" style={{ background: '#ffffff' }}>
            <div className="max-w-4xl mx-auto">
                <SubMenuWrapper>
                    <SubMenuItem icon={<ArrowBackIcon size={24} color="#6b7280" />} label={"Training Plans"} to={"/training-plans"} />
                </SubMenuWrapper>

                <div className="text-center mt-8">
                    <div className="rounded-3xl p-8" style={{
                        background: 'linear-gradient(135deg, #f1f5f9 0%, #fed7aa 100%)',
                        border: '1px solid rgba(249, 115, 22, 0.15)'
                    }}>
                        <span className="text-6xl mb-4 block">🔒</span>
                        <h2 className="text-2xl font-bold mb-4" style={{ color: '#1e293b' }}>
                            Members Only Training Plan
                        </h2>
                        <p className="text-base mb-6" style={{ color: '#64748b' }}>
                            Training plans are available to Momentum Members.
                        </p>
                        <Link
                            to="/membership"
                            className="inline-block rounded-2xl px-8 py-4 font-bold text-lg transition-all duration-300 hover:scale-[1.03] hover:shadow-2xl no-underline"
                            style={{
                                background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                                color: 'white',
                                boxShadow: '0 10px 40px rgba(249, 115, 22, 0.3)'
                            }}
                        >
                            Become a Member
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

function PlanCard({ plan, isMember }) {
    const cardContent = (
        <div
            className={`rounded-2xl p-5 md:p-6 transition-all duration-300 ${plan.comingSoon ? 'opacity-60' : 'hover:scale-[1.02] hover:shadow-xl cursor-pointer'
                }`}
            style={{
                background: plan.comingSoon
                    ? 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)'
                    : 'linear-gradient(135deg, #fff7ed 0%, #fed7aa 100%)',
                border: '1px solid rgba(249, 115, 22, 0.15)'
            }}
        >
            <div className="flex items-start gap-4">
                <span className="text-4xl">{plan.icon}</span>
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-bold m-0" style={{ color: '#1e293b' }}>
                            {plan.title}
                        </h3>
                        {plan.comingSoon && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{
                                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                color: 'white'
                            }}>
                                Coming Soon
                            </span>
                        )}
                        {!isMember && !plan.comingSoon && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{
                                background: 'rgba(15, 23, 42, 0.08)',
                                color: '#334155'
                            }}>
                                Members Only
                            </span>
                        )}
                    </div>
                    <p className="text-sm m-0" style={{ color: '#64748b' }}>
                        {plan.description}
                    </p>
                </div>
            </div>
        </div>
    );

    if (plan.comingSoon) {
        return cardContent;
    }

    return (
        <Link to={plan.path} className="no-underline">
            {cardContent}
        </Link>
    );
}
